import { useEffect, useRef, useState } from 'react';
import { portfolioService } from '../services/portfolioService';
export const inlineScopeKey = (scope) => JSON.stringify([
  scope.materialId, scope.sectionId || null, scope.locale, scope.revision,
]);
const scopePrefix = (scope) => JSON.stringify([scope.materialId, scope.sectionId || null, scope.locale]);
const empty = { draft: '', messages: [], suggestions: [], loading: false, error: '', review: false, needsReload: false };
export default function useInlineAnswers(store, scope, onDisabled) {
  const key = inlineScopeKey(scope);
  const prefix = scopePrefix(scope);
  const [state, setState] = useState(() => {
    if (store.has(key)) return { ...store.get(key), loading: false };
    const previous = [...store.values()].reverse().find((entry) => entry.prefix === scopePrefix(scope));
    return { ...empty, draft: previous?.draft || '', review: !!previous,
      notice: previous ? 'Content changed. Review the current content before sending.' : '' };
  });
  const request = useRef();
  const stateRef = useRef(state); stateRef.current = state;
  const update = (patch) => setState((old) => {
    const next = { ...old, ...patch };
    store.set(key, { ...next, prefix });
    return next;
  });
  useEffect(() => () => {
    request.current?.abort();
    store.set(key, { ...stateRef.current, loading: false, prefix });
  }, [key, store, prefix]);
  const submit = async () => {
    if (!state.draft.trim() || state.loading || state.review || state.invalid) return;
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    const submittedDraft = state.draft;
    const question = submittedDraft.trim();
    update({ loading: true, error: '' });
    try {
      const response = await portfolioService.question(scope.materialId, {
        question, locale: scope.locale, ...(scope.sectionId ? { sectionId: scope.sectionId } : {}),
      }, controller.signal);
      if (controller.signal.aborted) return;
      if (response.contentRevision !== scope.revision) {
        update({ messages: [], suggestions: [], review: true, needsReload: true,
          notice: 'Content changed. Reopen this material to review the current content.' });
        return;
      }
      if (!['matched', 'clarify', 'no_match'].includes(response.status)) throw new Error('Invalid answer');
      const text = response.status === 'matched' ? response.answerText :
        response.status === 'clarify' ? 'Please choose a question or add detail.' :
          'No stored answer matches this question.';
      update({ draft: stateRef.current.draft === submittedDraft ? '' : stateRef.current.draft, messages: [...state.messages, { question, text }],
        suggestions: response.suggestions || [], notice: '' });
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error.code === 'FEATURE_DISABLED') { onDisabled(); return; }
      if (error.code === 'CONTENT_REVISION_CHANGED') {
        update({ messages: [], suggestions: [], review: true, needsReload: true,
          notice: 'Content changed. Reopen this material to review the current content.' });
      } else {
        const text = error.status === 429 ? `Too many questions.${error.retryAfter ? ` Retry after ${error.retryAfter}.` : ''}` :
          error.code === 'NOT_FOUND' ? 'This question scope is unavailable.' :
            error.code === 'TEMPORARILY_UNAVAILABLE' ? 'Answers are temporarily unavailable.' :
              'Unable to answer. Your draft is retained. Try again.';
        update({ error: text, invalid: error.code === 'NOT_FOUND' });
      }
    } finally { if (!controller.signal.aborted) update({ loading: false }); }
  };
  return { ...state, setDraft: (draft) => update({ draft }), submit,
    reviewContent: () => update({ review: false, notice: '' }) };
}
